import { useState } from 'react';
import api from '../services/api';  // Changed from '../../services/api' to '../services/api'

export default function PortImport({ onImportComplete }) {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [result, setResult] = useState(null);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            const allowedTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
            if (!allowedTypes.includes(selectedFile.type)) {
                setError('Please upload an Excel file (.xlsx or .xls)');
                setFile(null);
                return;
            }
            setFile(selectedFile);
            setError('');
            setSuccess('');
        }
    };

    const handleImport = async () => {
        if (!file) {
            setError('Please select a file first');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');
        setResult(null);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await api.post('/ports/import', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            setResult(response.data);
            setSuccess(`Successfully imported ${response.data.imported} ports!`);

            if (response.data.skipped > 0) {
                setSuccess(prev => `${prev} (${response.data.skipped} skipped - already exist)`);
            }

            if (onImportComplete) {
                onImportComplete();
            }
        } catch (err) {
            console.error('Import error:', err);
            setError(err.response?.data?.error || 'Failed to import ports');
        } finally {
            setLoading(false);
            setFile(null);
            // Reset file input
            document.getElementById('fileInput').value = '';
        }
    };

    return (
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-slate-700">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                <svg className="w-6 h-6 text-[#3a663f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                </svg>
                Import Ports from Excel
            </h3>

            <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-xl p-6 text-center hover:border-[#3a663f] transition-colors">
                    <input
                        id="fileInput"
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileChange}
                        className="hidden"
                    />
                    <label
                        htmlFor="fileInput"
                        className="cursor-pointer flex flex-col items-center gap-2"
                    >
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <div>
                            <p className="text-sm font-medium text-gray-700 dark:text-slate-300">
                                {file ? file.name : 'Click to select an Excel file'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-slate-400">
                                Supported formats: .xlsx, .xls (Max 5MB)
                            </p>
                            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                                Expected columns: Port Name, Country (optional), Code (optional), Type (optional)
                            </p>
                        </div>
                    </label>
                </div>

                {file && (
                    <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm text-green-700 dark:text-green-300">{file.name}</span>
                        <span className="text-xs text-green-500 dark:text-green-400">
                            ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                    </div>
                )}

                {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 p-3 rounded-lg text-sm">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 p-3 rounded-lg text-sm">
                        {success}
                    </div>
                )}

                {result && result.errors && result.errors.length > 0 && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-400 p-3 rounded-lg text-sm">
                        <p className="font-semibold">Errors occurred for {result.errors.length} rows:</p>
                        <ul className="mt-1 list-disc list-inside max-h-32 overflow-y-auto">
                            {result.errors.slice(0, 5).map((err, idx) => (
                                <li key={idx}>{err.error}</li>
                            ))}
                            {result.errors.length > 5 && (
                                <li>... and {result.errors.length - 5} more</li>
                            )}
                        </ul>
                    </div>
                )}

                <button
                    onClick={handleImport}
                    disabled={!file || loading}
                    className={`w-full py-3 px-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${!file || loading
                            ? 'bg-gray-300 dark:bg-slate-600 text-gray-500 dark:text-slate-400 cursor-not-allowed'
                            : 'bg-[#3a663f] hover:bg-[#2d5232] text-white shadow-lg shadow-[#3a663f]/20 hover:shadow-xl'
                        }`}
                >
                    {loading ? (
                        <>
                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Importing...
                        </>
                    ) : (
                        <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                            Import Ports
                        </>
                    )}
                </button>

                <div className="text-xs text-gray-400 dark:text-slate-500">
                    <p>Example Excel format:</p>
                    <table className="mt-1 border-collapse border border-gray-300 dark:border-slate-600 text-xs">
                        <thead>
                            <tr className="bg-gray-50 dark:bg-slate-700">
                                <th className="border border-gray-300 dark:border-slate-600 px-2 py-1">Port Name</th>
                                <th className="border border-gray-300 dark:border-slate-600 px-2 py-1">Country</th>
                                <th className="border border-gray-300 dark:border-slate-600 px-2 py-1">Code</th>
                                <th className="border border-gray-300 dark:border-slate-600 px-2 py-1">Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="border border-gray-300 dark:border-slate-600 px-2 py-1">Mombasa</td>
                                <td className="border border-gray-300 dark:border-slate-600 px-2 py-1">Kenya</td>
                                <td className="border border-gray-300 dark:border-slate-600 px-2 py-1">KEMBA</td>
                                <td className="border border-gray-300 dark:border-slate-600 px-2 py-1">sea</td>
                            </tr>
                            <tr>
                                <td className="border border-gray-300 dark:border-slate-600 px-2 py-1">Nairobi</td>
                                <td className="border border-gray-300 dark:border-slate-600 px-2 py-1">Kenya</td>
                                <td className="border border-gray-300 dark:border-slate-600 px-2 py-1">NBO</td>
                                <td className="border border-gray-300 dark:border-slate-600 px-2 py-1">air</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}